 <!-- This is used by questiontypes to display inputs(not radio or checkbox). -->
<template>
	<div data-questioninput :question-id="questionData.id" :data-input-has-had-focus="hasHadFocus">
		<answerlabel
			v-if="answerData.label !== `` && showLabel"
			:for="id"
			:answerData="answerData"
			:questionData="questionData"
			:aria-hidden="ariaHidden"
			:showDescription="showDescription"
			:tab-index="labelCanBeFocused"
		/>
		<!-- Create either a textarea, inputfield or nothing based on the type of the answer -->
		<div data-input>
			<div data-input-container :data-input-type="answerData.htmlInputType" >
				<textarea
					v-if="answerData.htmlInputType === `textarea`"
					v-bind="inputAttributes"
					v-model="answerData.openField"
					@focus="inputReceivesFocus"
					@blur="inputLosesFocus"
					@keydown="inputCheck"
					@input="inputIsChanged"
				></textarea>
				<input
					v-else-if="answerData.htmlInputType === `number`"
					v-bind="inputAttributes"
					type="text"
					@focus="inputReceivesFocus"
					@blur="numberInputLosesFocus"
					@input="numberInputIsChanged"
					@keydown="numberKeyInput"
					v-model="answerData.openField"
				/>
				<template v-else-if="answerData.type === 'Birthday'">
					<input 
						:id="id"
						type="number" 
						placeholder="DD"
						pattern="[0-9]"
						step="1"
						min="1"
						max="31"
						ref="input"
						data-birthday-datepicker 
						v-model="birthday.day"
						:required="restrictions.required && !requiredShouldBeIgnored"
						:ariaRequired="restrictions.required && !requiredShouldBeIgnored"
						:dataHasValue="answerData.openField !== `` && answerData.isChecked"
						:disabled="disabled"
						@focus="inputReceivesFocus"
						@blur="numberInputLosesFocus"
					/>
					<input 
						:id="id + '-2'"
						type="number" 
						placeholder="MM" 
						pattern="[0-9]"
						step="1"
						min="1"
						max="12"
						ref="input"
						data-birthday-datepicker 
						v-model="birthday.month"
						:required="restrictions.required && !requiredShouldBeIgnored"
						:ariaRequired="restrictions.required && !requiredShouldBeIgnored"
						:dataHasValue="answerData.openField !== `` && answerData.isChecked"
						:disabled="disabled"
						@focus="inputReceivesFocus"
						@blur="numberInputLosesFocus"
					/>
					<input 
						:id="id + '-3'"
						type="number" 
						placeholder="YYYY"
						pattern="[0-9]"
						step="1"
						min="1900"
						max="2300"
						ref="input"
						data-birthday-datepicker 
						v-model="birthday.year"
						:required="restrictions.required && !requiredShouldBeIgnored"
						:ariaRequired="restrictions.required && !requiredShouldBeIgnored"
						:dataHasValue="answerData.openField !== `` && answerData.isChecked"
						:disabled="disabled"
						@focus="inputReceivesFocus"
						@blur="numberInputLosesFocus"
					/>
				</template>
				<input
					v-else
					v-bind="inputAttributes"
					v-model="answerData.openField"
					@focus="inputReceivesFocus"
					@blur="inputLosesFocus"
					@keydown="inputCheck"
					@input="inputIsChanged"
				/>
				<div 
					v-if="answerData.validation.validationType === 'CharactersMinMax' && answerData.validation.maxValue <= 2500"
					class="character-counter-container"
				>
					<p class="character-counter">
						<span :class="{ 'max-value-reached': maxValueIsReached}">
							{{ answerData.openField.length }}
						</span> 
						<span>/</span>
						<span>
							{{ answerData.validation.maxValue }}
						</span>
					</p>
				</div>
				<font-awesome-icon
					v-if="canUseSpeechRecognition"
					data-icon
					@click.stop.prevent="runSpeechRecognition"
					@keydown.enter.stop.prevent="runSpeechRecognition"
					icon="microphone"
					id="record-btn"
					:fontSize="30"
					:aria-label="$store.getters.textResource(`rs_start_recording_btn`)"
					tabindex="0"
					:class="{'is-recording': recordStateMessage !== ''}"
				/>
			</div>
			<span
				data-asterisk
				v-if="restrictions.required === true && this.requiredShouldBeIgnored === false"
			>*</span>
		</div>
		<div v-if="recordStateMessage !== '' && canUseSpeechRecognition">
			<p
				record-status-message
				aria-live="polite"
			>
				{{recordStateMessage}}
			</p>
		</div>
		<span
			v-if="validationMessage != ``"
			data-validation-message
			role="alert"
			:data-validation-type="validationType"
			:data-hide-validation="requiredShouldBeIgnored && answerData.isChecked === false"
		>{{ validationMessage }}</span>
	</div>
</template>

<script>
import answerlabel from "@/components/AnswerLabel";

export default {
	name: `question-input`,
	data() {
		return {
			validationMessage: ``,
			hasHadFocus: false,
			validationType: ``,
			recordStateMessage: '',
			birthday: {
				day: '',
				month: '',
				year: ''
			}
		};
	},
	watch: {
		birthday: {
			handler() {
				this.answerData.openField = `${this.birthday.year}-${this.birthday.month}-${this.birthday.day}`;
			},
			deep: true
		}
	},
	components: {
		answerlabel,
	},
	props: {
		// The data of the answer that this inputfield is part of
		answerData: {
			type: Object,
		},
		sqData: {
			type: Object,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		// Sometimes you don't want a field to be required. For example when a corresponding radio button hasn't been selected
		requiredShouldBeIgnored: {
			type: Boolean,
			default: false,
		},
		// Is the label allowed to be focusable. This is useful for when navigating a page with a keyboard
		labelCanBeFocused: {
			type: Boolean,
			default: false,
		},
		showLabel: {
			type: Boolean,
			default: true,
		},
		showDescription: {
			type: Boolean,
			default: false,
		},
		showPlaceholder: {
			type: Boolean,
			default: true
		},
		ariaHidden: {
			type: Boolean,
			default: false,
		},
		// The data of the question that this inputfield is part of
		questionData: {},
	},
	computed: {
		state: function () {
			return this.$store.state;
		},
		// The restrictions(like min, max, required) of the answer
		restrictions: function () {
			return this.answerData.validation;
		},
		textResource() {
			return this.$store.getters.textResource;
		},
		canUseSpeechRecognition() {
			return this.questionData.hasSpeechToText === true && window.webkitSpeechRecognition !== undefined;
		},
		// creates the Id's for the answers with inputs based on a single question or multiple subquestions
		id: function () {
			if (this.questionData.subQuestions) {
					return `question-${this.questionData.number}_sq-${this.sqData.number}_checkradio-answer-${this.answerData.number}`;
			} else return `question-${this.questionData.number}_checkradio-input-answer-${this.answerData.number}`;
		},
		// The attributes and their values that should be on the inputfield or textarea
		inputAttributes: function () {
			let restrictions = this.restrictions;
			let answerData = this.answerData;
			let questionData = this.questionData;
			let type = answerData.htmlInputType;
			let placeholder, min, max, minlength, maxlength;

			if (type === `date` || type === `number`) {
				placeholder = this.textResource(`rs_type_${type}_here_placeholder`);
			} else {
				placeholder = this.textResource(`rs_type_here_placeholder`);
			}
			
			if(restrictions.validationType === 'CharactersMinMax') {
				minlength = restrictions.minValue;
				maxlength = restrictions.maxValue;
				min = false;
				max = false;
			} else {
				maxlength = restrictions.maxLength > -1 ? restrictions.maxLength : 2500;
				minlength = restrictions.minLength > -1 ? restrictions.minLength : false;
				min = restrictions.minValue === -1 ? false : restrictions.minValue;
				max = restrictions.maxValue === -1 ? false : restrictions.maxValue
			}

			return {
				type: type,
				value: answerData.openField,
				maxlength: maxlength,
				minlength: minlength,
				min: min,
				max: max,
				pattern: restrictions.pattern ? restrictions.pattern : false,
				required: restrictions.required && !this.requiredShouldBeIgnored,
				"aria-required": restrictions.required && !this.requiredShouldBeIgnored,
				id: this.id,
				"data-has-value": answerData.openField !== `` && answerData.isChecked,
				"aria-labelled-by": `question-${questionData.number}_label-${answerData.number}`,
				disabled: this.disabled,
				placeholder: this.placeholder ? `\u270e ${placeholder}` : ``,
				ref: `input`,
			};
		},
		// Any validation messages that are returned from the server. These are then showed in the validationpopup component
		serverInputValidation() {
			if (this.state.message === undefined) return undefined;
			return this.state.message.filter((message) => message.answerId === this.answerData.id);
		},
		maxValueIsReached() {
			return this.answerData.openField.length >= this.answerData.validation.maxValue;
		}
	},
	methods: {
		inputReceivesFocus: function (e) {
			this.$emit(`element-focused`, e);
		},
		runSpeechRecognition: function() {
			const SpeechRecognition = window.webkitSpeechRecognition;
			const recognition = new SpeechRecognition();
			recognition.lang = this.state.surveyInfo.culture;

			recognition.onstart = () => {
				this.recordStateMessage = this.$store.getters.textResource(`rs_start_recording_message`);
			};

			recognition.onspeechend = () => {
				this.recordStateMessage = '';
				recognition.stop();
			}

			recognition.onerror = (event) => {
				this.recordStateMessage = `${event.error} - ${event.message}`;
			};

			// This runs when the speech recognition service returns result
			recognition.onresult = (event) => {
				const transcript = event.results[0][0].transcript;
				this.answerData.openField += " " + transcript;
				this.answerData.isEdited = true;
			};

			// start recognition
			recognition.start();
		},
		inputLosesFocus: function (e) {
			let validation = this.answerData.validation;
			let characterAmount = this.answerData.openField.length;
			let textResource = this.textResource;
			this.hasHadFocus = true;

			this.decideOnValidityType(e.target.validity);

			if (this.questionData.type === `Fields` || this.questionData.type === `Open`) {
				this.answerData.isChecked = this.answerData.openField !== ``;
			}

			// Check if there is a value when input is required.
			if (validation.required && e.target.value !== ``) {
				this.validationMessage = ``;
				e.target.setCustomValidity(``);
			}
			
			// If there is a pattern, we are expecting either a postcode or an email
			if (validation.validationType === `Date`) {
				if (e.target.validity.rangeUnderflow) {
					let date = new Date(validation.minValue).toLocaleDateString();
					this.validationMessage = textResource(`rs_value_date_too_early`) + ` ` + date;
				} else if (e.target.validity.rangeOverflow) {
					let date = new Date(validation.maxValue).toLocaleDateString();
					this.validationMessage = textResource(`rs_value_date_too_late`) + ` ` + date;
				}				
			} else if (e.target.validity.patternMismatch) {
				// The maxlength attribute indicates whether a postcode or an email is expected
				if (e.currentTarget.getAttribute(`maxlength`) === `100`) this.validationMessage = textResource(`rs_value_not_an_email`);
				else this.validationMessage = textResource(`rs_value_not_a_postcode`);
			} else if (validation.required && e.target.value === `` && this.answerData.htmlInputType === 'date') {
				this.validationMessage = `${textResource(`rs_type_date_here_placeholder`)}`;
				e.target.setCustomValidity(`${textResource(`rs_type_date_here_placeholder`)}`);
			} else if (validation.required && e.target.value === ``) {
				this.validationMessage = textResource(`rs_input_is_required`);
			} else if (characterAmount > this.inputAttributes.maxlength) {
				// We use this prevent posting past the character limit.
				this.validationMessage = `${textResource(`rs_input_maxlength_reached`).replace(`{0}`, this.inputAttributes.maxlength)}`;
				e.target.setCustomValidity(`${textResource(`rs_input_maxlength_reached`).replace(`{0}`, this.inputAttributes.maxlength)}`);
			} else if(characterAmount < this.inputAttributes.minlength) {
				this.validationMessage = `${textResource(`rs_input_minlength_reached`).replace(`{0}`, this.inputAttributes.minlength)}`;
				e.target.setCustomValidity(`${textResource(`rs_input_minlength_reached`).replace(`{0}`, this.inputAttributes.minlength)}`);
			} else {
				this.validationMessage = ``;
				e.target.setCustomValidity(``);
			}

			this.$emit(`element-loses-focus`, e);
		},
		// Do number validation. We can't use input number because then in Chrome because then you can't use . and ,
		numberInputLosesFocus: function (e) {
			let validation = this.answerData.validation;
			let value = parseFloat(e.target.value);
			let textResource = this.textResource;
			let min = parseInt(e.target.min || validation.minValue);
			let max = parseInt(e.target.max || validation.maxValue);
			let characterAmount = this.answerData.openField.length;
			this.hasHadFocus = true;

			this.decideOnValidityType(e.target.validity);

			if (this.questionData.type === `Fields` || this.questionData.type === `Open`) {
				this.answerData.isChecked = this.answerData.openField !== ``;
			}
			
			if (validation.validationType === `CharactersMinMax`) {
				if(characterAmount < min) {
					this.validationMessage = `${textResource(`rs_input_minlength_reached`).replace(`{0}`, min)}`;
					e.target.setCustomValidity(`${textResource(`rs_input_minlength_reached`).replace(`{0}`, min)}`);
				} else if(characterAmount > max) {
					this.validationMessage = `${textResource(`rs_input_maxlength_reached`).replace(`{0}`, max)}`;
					e.target.setCustomValidity(`${textResource(`rs_input_maxlength_reached`).replace(`{0}`, max)}`);
				}
			} else if (min > -1 && value < min) {
				this.validationMessage = `${textResource(`rs_number_input_value_too_low`)} ${min}`;
				e.target.setCustomValidity(`${textResource(`rs_number_input_value_too_low`)} ${min}`);
			} else if (max > -1 && value > max) {
				this.validationMessage = `${textResource(`rs_number_input_value_too_high`)} ${max}`;
				e.target.setCustomValidity(`${textResource(`rs_number_input_value_too_high`)} ${max}`);
			} else if (validation.required && e.target.value === `` && this.answerData.htmlInputType === 'number') {
				this.validationMessage = `${textResource(`rs_type_number_here_placeholder`)}`;
				e.target.setCustomValidity(`${textResource(`rs_type_number_here_placeholder`)}`);
			} else if (validation.required && e.target.value === `` && this.answerData.htmlInputType === 'date') {
				this.validationMessage = `${textResource(`rs_type_date_here_placeholder`)}`;
				e.target.setCustomValidity(`${textResource(`rs_type_date_here_placeholder`)}`);
			} else if (validation.required && e.target.value === ``) {
				this.validationMessage = `${textResource(`rs_input_is_required`)}`;
				e.target.setCustomValidity(`${textResource(`rs_input_is_required`)}`);
			} else if (isNaN(value) && e.target.value !== ``) {
				this.validationMessage = `${textResource(`rs_input_not_a_number`)}`;
				e.target.setCustomValidity(`${textResource(`rs_input_not_a_number`)}`);
			} else if(this.answerData.type === 'Birthday' && this.birthday.day !== '' && this.birthday.month !== '' && this.birthday.year !== '') {
				const datePutInByUser = new Date(this.answerData.openField);
				const minDateRestriction = new Date(this.restrictions.minValue);
				const maxDateRestriction = new Date(this.restrictions.maxValue);

				if(isNaN(datePutInByUser)) {
					this.validationMessage = `${textResource(`rs_invalid_date`)}`;
					e.target.setCustomValidity(`${textResource(`rs_invalid_date`)}`);
				} else {
					if (this.restrictions.minValue !== -1 && datePutInByUser < minDateRestriction) {
						this.validationMessage = textResource(`rs_value_date_too_early`) + ` ` + minDateRestriction.toLocaleDateString();
						e.target.setCustomValidity(textResource(`rs_value_date_too_early`) + ` ` + minDateRestriction.toLocaleDateString());
					} else if (this.restrictions.maxValue !== -1 && datePutInByUser > maxDateRestriction) {
						this.validationMessage = textResource(`rs_value_date_too_late`) + ` ` + maxDateRestriction.toLocaleDateString();
						e.target.setCustomValidity(textResource(`rs_value_date_too_late`) + ` ` + maxDateRestriction.toLocaleDateString());
					} else {
						this.validationMessage = ``;
						e.target.setCustomValidity(``); // Empty custom validity = valid
					}
				} 
			} else {
				this.validationMessage = ``;
				e.target.setCustomValidity(``); // Empty custom validity = valid
			}

			this.$emit(`element-loses-focus`, e);
		},
		// Validity type tells what exactly is going wrong with the input of the user. This is needed for the UI tests
		decideOnValidityType: function (validity) {
			for (let valKey in validity) {
				if (validity[valKey] === true) {
					this.validationType = valKey;
				}
			}
		},
		numberInputIsChanged: function (e) {
			let newValue = this.answerData.openField.replace(/[^0-9.,-]+/g, ``); // Remove everything that is not a number or .,-
			let indexOfDot = newValue.indexOf(`.`);
			let indexOfComma = newValue.indexOf(`,`);
			let decimals = this.answerData.validation.numberScale;

			// We have to add +1 because we also have to allow for the dot/comma. We cut everything when decimals are not allowed
			if (decimals !== -1) decimals += 1;
			else decimals = 0;

			// Cut of the end of the string that is out of bounds based on either the first dot or first comma
			if (indexOfDot !== -1) newValue = newValue.substring(0, indexOfDot + decimals);
			else if (indexOfComma !== -1) newValue = newValue.substring(0, indexOfComma + decimals);

			this.answerData.openField = newValue;
			this.$emit(`element-receives-input`, e);
		},
		numberKeyInput: function (e) {
			let dotPresent = this.answerData.openField.indexOf(`.`);
			let commaPresent = this.answerData.openField.indexOf(`,`);
			let dashPresent = this.answerData.openField.indexOf(`-`);
			let openFieldFirstChar = this.answerData.openField.charAt(0);
			let textResource = this.textResource;

			if (this.answerData.openField.length >= this.inputAttributes.maxlength) {
				this.validationMessage = `${textResource(`rs_input_maxlength_reached`).replace(`{0}`, this.inputAttributes.maxlength)}`;
				e.target.setCustomValidity(`${textResource(`rs_input_maxlength_reached`).replace(`{0}`, this.inputAttributes.maxlength)}`);
			} else {
				this.validationMessage = ``;
				e.target.setCustomValidity(``); // Empty custom validity = valid
			}

			// Don't allow both comma or period at the same time
			if ((e.code === `Period` || e.code === `NumpadDecimal`) && (commaPresent !== -1 || dotPresent !== -1 || dashPresent !== -1)) e.preventDefault();
			if (e.code === `Comma` && (dotPresent !== -1 || commaPresent !== -1 || dashPresent !== -1)) e.preventDefault();
			// If answer allready has a minus at the start then prevent it.
			if ((e.code === `Minus` || e.code === `NumpadSubtract`) && (dashPresent !== -1 || openFieldFirstChar === "-")) e.preventDefault();
			// If answer is empty and has no minus at the start then allow it only at the start.
			if ((e.code === `Minus` || e.code === `NumpadSubtract`) && openFieldFirstChar !== "-" && openFieldFirstChar !== "") e.preventDefault();
		},
		inputCheck: function (e) {
			let textResource = this.textResource;

			if (this.answerData.openField.length >= this.inputAttributes.maxlength) {
				this.validationMessage = `${textResource(`rs_input_maxlength_reached`).replace(`{0}`, this.inputAttributes.maxlength)}`;
				e.target.setCustomValidity(`${textResource(`rs_input_maxlength_reached`).replace(`{0}`, this.inputAttributes.maxlength)}`);
			} else {
				this.validationMessage = ``;
				e.target.setCustomValidity(``); // Empty custom validity = valid
			}
		},
		inputIsChanged: function (e) {
			this.$emit(`element-receives-input`, e);
		},
		focusOnInput() {
			if (this.$refs.input) this.$refs.input.focus();
		},
	},
	mounted: function () {
		let textResource = this.textResource;
		let target = this.$refs.input;

		if (target === undefined) return;

		if (target.validity.patternMismatch) {
			// The maxlength attribute indicates whether a postcode or an email is expected
			if (target.getAttribute(`maxlength`) === `100`) this.validationMessage = textResource(`rs_value_not_an_email`);
			else this.validationMessage = textResource(`rs_value_not_a_postcode`);
		} else {
			this.validationMessage = target.validationMessage;
		}

		if (this.answerData.type === 'Birthday') {
			const openFieldDate = new Date(this.answerData.openField);

			if (!isNaN(openFieldDate)) {
				this.birthday.year = openFieldDate.getFullYear();
				this.birthday.month = openFieldDate.getMonth() + 1;
				this.birthday.day = openFieldDate.getDate();
			}

			`${this.birthday.year}-${this.birthday.month}-${this.birthday.day}`;
		}

		//  Process validation that is returned from the server, and make them visible
		if (this.serverInputValidation !== undefined && this.serverInputValidation.length !== 0) {
			this.validationMessage = this.serverInputValidation[0].text;
			this.hasHadFocus = true;
			this.$refs.input.setCustomValidity(this.serverInputValidation[0].message); // Make the field invalid
		}
	},
};
</script>

<style scoped>
@import "./../styles/variables.css";

[data-questioninput] {
	display: flex;
	position: relative;
	width: 100%;
	flex-direction: column;

	> *:nth-child(2) {
		margin-top: 15px;
	}
}

.character-counter-container {
	width: 100%;
	display: flex;
	justify-content: flex-end;

	.character-counter {
		font-size: 12px;
    	margin-block: 5px;
	}

	.max-value-reached {
		color: var(--primary-color);
	}
}

[data-input-container] {
	width: 100%;
	position: relative;
}

[data-input] {
	display: flex;
	position: relative;

	[data-asterisk] {
		display: flex;
		justify-content: center;
		align-items: flex-start;
		margin-left: 8px;
	}
}

[data-birthday-datepicker] {
    margin: 0 5px;
}

[data-labels-next-to-answers] [data-questioninput] {
	flex-direction: row;
	flex-wrap: wrap;
	justify-content: space-between;
}

[data-labels-next-to-answers] [data-questioninput] > label {
	width: 20%;
	padding-right: 5px;
}

[data-labels-next-to-answers] [data-questioninput] [data-input] {
	width: 80%;
	margin-top: 0;
	margin-bottom: 15px;
}
[data-labels-next-to-answers] [data-questioninput] [data-validation-message] {
	width: 80%;
	margin-left: 20%;
}

[data-questioninput]:has(.character-counter-container) {
	[data-validation-message] { 
		position: relative;
		top: -30px;
	}
}

[data-questioninput] [data-input-type="textarea"]:has(.character-counter-container) #record-btn {
	top: 35%;
}
[data-questioninput] [data-input-type="text"]:has(.character-counter-container) #record-btn {
	top: 26%;
}

[data-answer-input] [data-input-has-had-focus="true"]:has([data-hide-validation="true"]) [data-input] input:invalid {
	border-bottom: 1px solid #333;
}

[data-input-has-had-focus="true"],
[data-next-button-clicked="true"] [fields] {
	input:invalid,
	textarea:invalid {
		border-bottom: 1px solid red;
		margin-bottom: -1px;
	}

	[data-validation-message] {
		display: block;
		color: red;
		margin-top: 7px;
		font-size: 16px;

		&[data-hide-validation] {
			display: none;
		}
	}
}

#record-btn {
	background: none;
	border-width: 0;
	position: absolute;
   right: 10px;
	font-size: 25px;
	top: 50%;
	transform: translateY(-50%);
	cursor: pointer;
	transition: 0.2s;

	&.is-recording {
		transform: translateY(-50%) scale(1.1);

		path {
			color: red;
		}
	}

	path {
		color: #333;
	}

	&:hover {
		transform: translateY(-50%) scale(1.1);
	}
}

[data-validation-message] {
	display: none;
}

@media only screen and (max-width: 550px) {
	[data-labels-next-to-answers] [data-questioninput] {
			flex-direction: column;
	}

	[data-labels-next-to-answers] [data-questioninput] > label {
		width: auto;
	}
	[data-labels-next-to-answers] [data-questioninput] [data-input] { 
		width: 100%;
	}
	[data-labels-next-to-answers] [data-questioninput] [data-validation-message] {
		width: 100%;
	}

	[data-labels-next-to-answers] [data-questioninput]		
		input {
		margin-top: 15px;
		margin-bottom: 0;
	}

	[data-labels-next-to-answers] [data-questioninput] textarea {
		margin-top: 15px;
		margin-bottom: 0;
	}

	[data-labels-next-to-answers] [data-questioninput] [data-validation-message] {
		width: 100%;
		margin-left: auto;
	}	
}
</style>
